import { useSelector } from "react-redux";
import { RootState } from "app/store";
import i18n from "features/parent/i18n";

/**
 * カードスタイルの使用状況コンポーネント（プレイヤー数・メッセージ数用）
 */
const UsageCard = ({
  label,
  used,
  limit,
  unlimited,
  showLimit = true,
  suffix,
  icon,
  iconBgColor,
  iconColor,
}: {
  label: string;
  used: number;
  limit: number;
  unlimited: boolean;
  showLimit?: boolean;
  suffix?: string;
  icon: string;
  iconBgColor: string;
  iconColor: string;
}) => {
  return (
    <div className="bg-white rounded-[16px] p-5 flex-row-el gap-4 hover:-translate-y-0.5 hover:shadow-lg transition-all">
      <div
        className="w-12 h-12 rounded-[12px] flex items-center justify-center"
        style={{ backgroundColor: iconBgColor }}
      >
        <span className="material-symbols-outlined text-[42px]" style={{ color: iconColor }}>
          {icon}
        </span>
      </div>
      <div className="flex-1">
        <div className="text-[42px] text-textcolor-black">
          {unlimited ? (
            <span className="text-blue-100">{i18n.t("MSG_PARENT_PAGE_UNLIMITED")}</span>
          ) : (
            <>
              {used}
              {suffix && <span className="text-[24px] font-normal">{suffix}</span>}
              {showLimit && (
                <span className="text-[24px] font-normal text-gray2-100">/{limit}</span>
              )}
            </>
          )}
        </div>
        <div className="text-[19px] text-gray2-100 mt-0.5">{label}</div>
      </div>
    </div>
  );
};

/**
 * プログレスバーコンポーネント（素材容量用）
 */
const UsageBar = ({
  label,
  used,
  limit,
  usedBytes,
  limitBytes,
  unlimited,
}: {
  label: string;
  used: string;
  limit: string;
  usedBytes: number;
  limitBytes: number;
  unlimited: boolean;
}) => {
  const percentage =
    unlimited || limitBytes <= 0 ? 0 : Math.min(100, (usedBytes / limitBytes) * 100);

  return (
    <div className="flex-col-el gap-2 py-3 border-b border-outline last:border-b-0">
      <div className="flex-row-el items-center justify-between">
        <div className="text-[24px] text-textcolor-black">{label}</div>
        <div className="text-[24px] text-textcolor-black">
          {unlimited ? (
            <span className="text-blue-100">{i18n.t("MSG_PARENT_PAGE_UNLIMITED")}</span>
          ) : (
            <>
              <span>{used}</span>
              <span className="text-gray2-100"> / {limit}</span>
            </>
          )}
        </div>
      </div>
      {!unlimited && (
        <div className="w-full h-[12px] bg-gray2-40 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all bg-blue-100"
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}
    </div>
  );
};

/**
 * バイトをMB表記に変換
 */
const formatBytes = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1000) {
    return `${(mb / 1024).toFixed(1)}GB`;
  }
  return `${mb.toFixed(0)}MB`;
};

/**
 * 利用状況セクション
 */
export const UsageSection = () => {
  const accountLimits = useSelector((state: RootState) => state.subscription.accountLimits);

  const { players, assets, chatHistoryLimit } = accountLimits;

  return (
    <div className="bg-white rounded-[16px] p-6">
      <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
        <span className="material-symbols-outlined !text-[45px] text-red-100">data_usage</span>
        {i18n.t("MSG_PARENT_PAGE_USAGE_SECTION_TITLE")}
      </div>

      {/* プレイヤー数とチャット履歴: 横並びカードスタイル */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <UsageCard
          label={i18n.t("MSG_PARENT_PAGE_USAGE_PLAYERS")}
          used={players.used}
          limit={players.limit}
          unlimited={players.unlimited}
          icon="group"
          iconBgColor="rgba(76,157,241,0.15)"
          iconColor="#4C9DF1"
        />
        <UsageCard
          label={i18n.t("MSG_PARENT_PAGE_USAGE_CHAT_HISTORY")}
          used={chatHistoryLimit.used}
          limit={chatHistoryLimit.limit}
          unlimited={false}
          showLimit={false}
          suffix={i18n.t("MSG_PARENT_PAGE_USAGE_ITEMS")}
          icon="chat"
          iconBgColor="rgba(43,220,152,0.15)"
          iconColor="#2BDC98"
        />
      </div>

      {/* 素材容量: 従来のプログレスバースタイル */}
      <UsageBar
        label={i18n.t("MSG_PARENT_PAGE_USAGE_STORAGE")}
        used={formatBytes(assets.used)}
        limit={formatBytes(assets.limit)}
        usedBytes={assets.used}
        limitBytes={assets.limit}
        unlimited={assets.unlimited}
      />
    </div>
  );
};
